import {
  selectUserInfo,
  selectUserRole,
  setActiveRole,
} from "../../../src/redux/app";
import { useAppDispatch, useAppSelector } from "../../../src/redux/hooks";
import { useRef, useState } from "react";

import { getPortalLandingPath } from "./util/portalRoutes";
import useClickOutside from "@verse_core/shared_components/useClickOutside";

const PORTAL_DISPLAY_NAMES: Record<string, string> = {
  provider: "Provider Portal",
  hand_off_partner: "Partner Portal",
  manufacturer: "Manufacturer Portal",
  analytics: "Analytics Portal",
};

interface PortalSwitcherProps {
  loggedIn: boolean;
}

export default function PortalSwitcher(props: PortalSwitcherProps) {
  const userInfo = useAppSelector(selectUserInfo);
  const activeRole = useAppSelector(selectUserRole);
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => setIsOpen(false));

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const roles = userInfo?.roles || [];

  // Don't render if not logged in or only one role
  if (!props.loggedIn || roles.length <= 1) {
    return null;
  }

  const handlePortalSwitch = (role: string) => {
    dispatch(setActiveRole(role));
    window.location.href = getPortalLandingPath(role);
  };

  return (
    <div className="tw-relative tw-ml-[10px] tw-py-[15px]" ref={containerRef}>
      <button
        className="tw-font-500 tw-rounded-md tw-border-[1px] tw-border-solid tw-border-gray-5 tw-bg-white tw-px-[15px] tw-pb-[10px] tw-pt-[10px] tw-text-[16px] tw-text-black tw-no-underline hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
        onClick={handleToggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        title="Switch Portal"
      >
        <i className="fa fa-users"></i>
      </button>
      {isOpen && (
        <div
          className="tw-absolute tw-right-0 tw-z-10 tw-w-[300px] tw-rounded-md tw-border-[0.5px] tw-border-solid tw-border-gray-5 tw-bg-white tw-shadow-lg"
          id="portal-switcher-dropdown"
        >
          {/* Current portal */}
          <div className="tw-border-0 tw-border-b-[0.5px] tw-border-solid tw-border-blue-400 tw-bg-gray-300 tw-p-[13px] tw-pb-[8px]">
            <div className="tw-mb-[5px] tw-text-[8px] tw-text-gray-600">
              Current Portal
            </div>
            <div className="tw-font-medium tw-text-custom-blue">
              {PORTAL_DISPLAY_NAMES[activeRole] || activeRole}
            </div>
          </div>

          {/* Other portal options */}
          {roles
            .filter((role) => role !== activeRole)
            .map((role) => (
              <button
                key={role}
                className="tw-block tw-w-full tw-border-0 tw-bg-transparent tw-text-left hover:tw-bg-light-grey"
                onClick={() => handlePortalSwitch(role)}
              >
                <div className="tw-cursor-pointer tw-p-[13px]">
                  <div className="tw-text-custom-blue">
                    {PORTAL_DISPLAY_NAMES[role] || role}
                  </div>
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
